// Coalescing gate for concurrent microphone starts (SAL-2803).
//
// Two paths can legitimately race to start the microphone — e.g. the
// simulator's hands-free auto-start (after the persona greeting) vs. the
// user clicking the dock's "Start mic" button, or a double-tap of the mic
// button. Joining the in-flight attempt gives both callers the same working
// microphone; throwing at the second caller surfaced a "Microphone Error"
// toast while voice capture actually came up fine.

export const MICROPHONE_BUSY_ERROR_NAME = "MicrophoneBusyError";

export function isMicrophoneBusyError(error) {
  return error?.name === MICROPHONE_BUSY_ERROR_NAME;
}

export function createMicrophoneBusyError() {
  const busyError = new Error(
    "Microphone is still initializing. Please try again in a moment."
  );
  busyError.name = MICROPHONE_BUSY_ERROR_NAME;
  return busyError;
}

/**
 * Run `performStart(recordConversation, deviceId)` unless a start attempt is
 * already in flight in `store` (a `{ current }` holder, e.g. a React ref):
 * a concurrent call with the same configuration joins the in-flight attempt
 * and shares its result; a call with a different configuration (another
 * device, another recording mode) cannot be satisfied by it and throws
 * MicrophoneBusyError. The store is cleared once the attempt settles.
 */
export function coalesceMicrophoneStart(
  store,
  recordConversation,
  deviceId,
  performStart
) {
  const inFlight = store.current;
  if (inFlight) {
    if (
      inFlight.recordConversation === recordConversation &&
      inFlight.deviceId === deviceId
    ) {
      return inFlight.promise;
    }
    throw createMicrophoneBusyError();
  }

  const attempt = { recordConversation, deviceId };
  attempt.promise = performStart(recordConversation, deviceId).finally(() => {
    // Only clear our own attempt — a newer start may already occupy the store.
    if (store.current === attempt) {
      store.current = null;
    }
  });
  // Publish only the fully-formed attempt so no caller can observe a missing
  // promise.
  store.current = attempt;

  return attempt.promise;
}
